import {
  ChartLineUp,
  CursorClick,
  DownloadSimple,
  HandCoins as HandCoinsIcon,
  Megaphone,
  Money,
  TrendUp,
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import { Badge } from "flowbite-react";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import AnimatedModal from "../../../../components/AnimatedModal";
import InlineEdit from "../../../../components/InlineEdit";
import CampaignDetailSkeleton from '../../../../components/SkeletonLoaders/CampaignDetailSkeleton';
import { useDateRange } from "../../../../hooks";
import { useAppleAdsCampaignDetail } from "../../../../hooks/get/useAppleAdsCampaignDetail";
import { useAppleAdsUpdateCampaign } from "../../../../hooks/patch/useAppleAdsUpdateCampaign";
import { IDateRangePreset } from "../../../../interfaces";
import { cn, getAmountByCurrency, getCurrencySymbol } from "../../../../lib/utils";
import {
  APPLE_CAMPAIGN_STATUS,
  CurrencyCode,
} from "../../../../shared/constants";
import PerformanceChart from "../../components/PerformanceChart";
import BaseCampaignDetail from "../../components/shared/BaseCampaignDetail";
import StatusSelect from "../../../../components/StatusSelect";

const AppleAdsCampaignDetail: React.FC = () => {
  const { campaign_id } = useParams<{ campaign_id: string }>();
  const navigate = useNavigate();
  const { dateRange, setDateRange, selectedPreset, setSelectedPreset } = useDateRange();
  const { t } = useTranslation();

  // Modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<APPLE_CAMPAIGN_STATUS | null>(null);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [pendingBudget, setPendingBudget] = useState<string>("");
  const [showBidModal, setShowBidModal] = useState(false);
  const [pendingBid, setPendingBid] = useState<string>("");
  const [isActionLoading, setIsActionLoading] = useState(false);

  const updateCampaignMutation = useAppleAdsUpdateCampaign();

  const handleDateRangeChange = (start: Date, end: Date) => {
    setDateRange(start, end);
  };

  const handlePresetChange = (preset: IDateRangePreset | undefined) => {
    setSelectedPreset(preset);
  };

  const { data, isLoading, isError, refetch } = useAppleAdsCampaignDetail({
    campaignId: campaign_id || "",
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const campaignDetail = data;
  const campaign = campaignDetail?.campaign;
  const currency = ((campaign?.default_currency || campaign?.currency) as CurrencyCode) || "USD";
  const metrics = campaignDetail?.metrics; // Metrics are now flattened on the campaign object

  const handleUpdateBudget = (newValue: string) => {
    setPendingBudget(newValue);
    setShowBudgetModal(true);
    return Promise.resolve();
  };

  const handleConfirmBudgetChange = async () => {
    setIsActionLoading(true);
    try {
      await updateCampaignMutation.mutateAsync({
        campaignId: campaign_id || "",
        data: { daily_budget: Number(pendingBudget) * 1000000 },
      });
      toast.success(
        t(
          "campaigns.manageCampaign.budgetUpdatedSuccessfully",
          "Budget updated successfully!",
        ),
      );
      setShowBudgetModal(false);
      refetch();
    } catch {
      toast.error(
        t(
          "campaigns.manageCampaign.errors.failedToUpdateBudget",
          "Failed to update budget",
        ),
      );
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleUpdateCptBid = (newValue: string) => {
    setPendingBid(newValue);
    setShowBidModal(true);
    return Promise.resolve();
  };

  const handleConfirmBidChange = async () => {
    setIsActionLoading(true);
    try {
      await updateCampaignMutation.mutateAsync({
        campaignId: campaign_id || "",
        data: { default_cpt_bid: Number(pendingBid) * 1000000 },
      });
      toast.success(
        t(
          "campaigns.manageCampaign.bidUpdatedSuccessfully",
          "Bid updated successfully!",
        ),
      );
      setShowBidModal(false);
      refetch();
    } catch {
      toast.error(
        t(
          "campaigns.manageCampaign.errors.failedToUpdateBid",
          "Failed to update bid",
        ),
      );
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleStatusSwitch = (value: APPLE_CAMPAIGN_STATUS) => {
    setPendingStatus(value);
    setShowStatusModal(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingStatus) return;
    setIsActionLoading(true);
    try {
      await updateCampaignMutation.mutateAsync({
        campaignId: campaign_id || "",
        data: { status: pendingStatus },
      });
      toast.success(
        t(
          "campaigns.manageCampaign.statusUpdatedSuccessfully",
          "Status updated successfully!",
        ),
      );
      setShowStatusModal(false);
      refetch();
    } catch {
      toast.error(
        t(
          "campaigns.manageCampaign.errors.failedToUpdateStatus",
          "Failed to update status",
        ),
      );
    } finally {
      setIsActionLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoading && isError) {
      toast.error(t("campaigns.errors.integrationChangedRedirect", "Campaign not found for the selected integration. Redirecting you back to the campaign list."));
      navigate(-1);
    }
  }, [isLoading, isError, navigate, t]);

  if (!isLoading && isError) return null;

  if (isLoading) return <CampaignDetailSkeleton />;

  return (
    <BaseCampaignDetail
      headerProps={{
        title: campaign?.apple_campaign_name || t("campaigns.detail.title", "Campaign Details"),
        platform: "apple_ads",
        type: "detail",
        appInfo: {
          id: campaign?.apple_campaign_id || "",
          name: campaign?.app_name || "",
          logoUrl: campaign?.app_logo || null,
        },
        pillSwitch: (
          <StatusSelect
            value={
              (campaign?.apple_campaign_status || "PAUSED") === "ENABLED"
                ? APPLE_CAMPAIGN_STATUS.ENABLED
                : APPLE_CAMPAIGN_STATUS.PAUSED
            }
            options={[
              {
                label: t("common.manageCampaign.paused", "Paused"),
                value: APPLE_CAMPAIGN_STATUS.PAUSED,
                color: "red",
              },
              {
                label: t("common.manageCampaign.enabled", "Enabled"),
                value: APPLE_CAMPAIGN_STATUS.ENABLED,
                color: "green",
              },
            ]}
            onChange={handleStatusSwitch}
          />
        ),
        onHistoryClick: () => navigate(`/campaigns/apple-search-ads/${campaign_id}/history`),
        onDateRangeChange: handleDateRangeChange,
        onPresetChange: handlePresetChange,
        dateRange: dateRange,
        selectedPresetId: selectedPreset?.id,
      }}
      statsCounts={7}
      stats={[
        {
          title: t("campaigns.metrics.totalSpend", "Total Spend"),
          value: getAmountByCurrency(metrics || {}, "total_spend", currency),
          isCurrency: true,
          icon: <Money size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />,
        },
        {
          title: t("campaigns.metrics.installs", "Installs"),
          value: metrics?.total_installs || 0,
          icon: <DownloadSimple size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />,
        },
        {
          title: t("campaigns.appleAds.taps", "Taps"),
          value: metrics?.total_taps || 0,
          icon: <CursorClick size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />,
        },
        {
          title: t("campaigns.appleAds.ttr", "TTR"),
          value: (metrics?.average_ttr || 0) * 100,
          isPercentage: true,
          icon: <TrendUp size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />,
        },
        {
          title: t("campaigns.appleAds.newDownloads", "New Downloads"),
          value: metrics?.total_new_downloads || 0,
          icon: <DownloadSimple size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />,
        },
        {
          title: t("campaigns.appleAds.conversionRate", "Conv. Rate"),
          value: (metrics?.average_conversion_rate || 0) * 100,
          isPercentage: true,
          icon: <ChartLineUp size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />,
        },
        {
          title: t("campaigns.appleAds.avgCpt", "Avg. CPT"),
          value: getAmountByCurrency(metrics || {}, "average_cpt", currency),
          isCurrency: true,
          icon: <HandCoinsIcon size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />,
        },
      ]}
      currency={currency as CurrencyCode}
      chart={
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold flex items-center gap-2 text-gray-800 dark:text-gray-200">
              <ChartLineUp size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />
              {t("campaigns.detail.performanceOverTime", "Performance Over Time")}
            </h3>
          </div>
          <div className="h-[350px]">
            {(campaignDetail?.daily_metrics || []).length > 0 ? (
              <PerformanceChart
                height={350}
                currency={currency}
                currencySymbol={getCurrencySymbol(currency as CurrencyCode)}
                data={(campaignDetail?.daily_metrics || []).map((m) => {
                  const rowSpend = getAmountByCurrency(m, "spend", currency);
                  return {
                    ...m,
                    clicks: m.taps || 0,
                    reach: 0,
                    country_code: m.country_or_region || "",
                    spend_usd: rowSpend,
                    ctr: m.taps && m.impressions ? (m.taps / m.impressions) * 100 : 0,
                    cpc_usd: m.avg_cpt_usd || (m.taps ? rowSpend / m.taps : 0),
                    cpm_usd: m.impressions ? (rowSpend / m.impressions) * 1000 : 0,
                  };
                })}
              />
            ) : (
              <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-neutral-800 rounded-xl border border-dashed border-gray-200 dark:border-neutral-700">
                <span className="text-sm text-gray-400 dark:text-gray-500">
                  {t("campaigns.detail.noDailyMetrics", "No daily metrics available for this period")}
                </span>
              </div>
            )}
          </div>
        </div>
      }
      sidebar={
        <div className="flex flex-col gap-6">
          <h3 className="text-sm font-bold flex items-center gap-2 text-gray-800 dark:text-gray-200">
            <TrendUp size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />
            {t("campaigns.detail.campaignSettings", "Campaign Settings")}
          </h3>
          <div className="flex flex-col gap-4">
            <InfoRow
              label={t("campaigns.detail.dailyBudget", "Daily Budget")}
              value={
                <InlineEdit
                  value={getAmountByCurrency(campaign || {}, "daily_budget", currency)}
                  onSave={handleUpdateBudget}
                  prefix={getCurrencySymbol(currency as CurrencyCode)}
                  type="number"
                  editIconPlacement="start"
                  alwaysShowEdit
                />
              }
            />
            <InfoRow
              label={t("appleAds.cptBid", "CPT Bid")}
              value={
                <InlineEdit
                  value={getAmountByCurrency(campaign || {}, "default_cpt_bid", currency) || getAmountByCurrency(metrics || {}, "average_cpt", currency)}
                  onSave={handleUpdateCptBid}
                  prefix={getCurrencySymbol(currency as CurrencyCode)}
                  type="number"
                  editIconPlacement="start"
                  alwaysShowEdit
                />
              }
            />
            <InfoRow
              label={t("campaigns.detail.objective", "Objective")}
              value={campaign?.apple_supply_source || "SEARCH"}
              capitalize
            />
            <InfoRow
              label={t("campaigns.detail.buyingType", "Buying Type")}
              value="AUCTION"
            />
            <InfoRow
              label={t("campaigns.detail.startDate", "Start Date")}
              value={campaign?.apple_campaign_start_time ? dayjs(campaign.apple_campaign_start_time).format("DD MMM YYYY") : "-"}
            />
            <InfoRow
              label={t("campaigns.detail.endDate", "End Date")}
              value={campaign?.apple_campaign_end_time ? dayjs(campaign.apple_campaign_end_time).format("DD MMM YYYY") : t("common.ongoing", "Ongoing")}
            />
            {campaign?.apple_countries_or_regions && campaign.apple_countries_or_regions.length > 0 && (
              <div className="flex flex-col gap-2 pt-2 border-t border-gray-50 dark:border-neutral-700">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {t("campaigns.detail.targetRegions", "Target Regions")}
                </span>
                <div className="flex wrap gap-1.5">
                  {campaign.apple_countries_or_regions.map((region: string) => (
                    <Badge key={region} color="green" size="xs" className="px-2 py-0.5 rounded-md font-bold">
                      {region}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {campaign?.apple_adam_id ? (
              <InfoRow
                label={t("appleAds.adamId", "Adam ID")}
                value={campaign.apple_adam_id}
              />
            ) : null}
          </div>
        </div>
      }
    >
      {/* Ad Groups */}
      {campaignDetail?.adgroups && campaignDetail.adgroups.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-sm border border-gray-200 dark:border-neutral-700 p-6 flex flex-col gap-4">
          <h3 className="text-sm font-bold flex items-center gap-2 text-gray-800 dark:text-gray-100">
            <Megaphone size={18} className="text-gray-700 dark:text-gray-300" weight="duotone" />
            {t("campaigns.appleAds.adGroups", "Ad Groups")}
            <Badge color="indigo" size="xs" className="ml-1">{campaignDetail.adgroups.length}</Badge>
          </h3>
          <div className="flex flex-col gap-3">
            {campaignDetail.adgroups.map((ag) => {
              const isEnabled = ag.apple_adgroup_status === "ENABLED";
              const devices: string[] = ag.apple_targeting_dimensions?.deviceClass?.included || [];
              return (
                <div key={ag.id} className="border border-gray-100 dark:border-neutral-700 rounded-xl p-4 flex flex-col gap-3 bg-gray-50/50 dark:bg-neutral-900/60">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 leading-snug">{ag.apple_adgroup_name}</span>
                    <Badge color={isEnabled ? "success" : "warning"} size="xs" className="shrink-0 font-bold uppercase">
                      {ag.apple_adgroup_status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                    {ag.default_cpt_bid_usd != null && (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">CPT Bid</span>
                        <span className="text-sm font-bold text-gray-700 dark:text-gray-300">{getCurrencySymbol(currency as CurrencyCode)}{ag.default_cpt_bid_usd.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Automation</span>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{ag.apple_automation_type}</span>
                    </div>
                    {ag.apple_adgroup_start_time && (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Start Date</span>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{dayjs(ag.apple_adgroup_start_time).format("DD MMM YYYY")}</span>
                      </div>
                    )}
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">End Date</span>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {ag.apple_adgroup_end_time ? dayjs(ag.apple_adgroup_end_time).format("DD MMM YYYY") : t("common.ongoing", "Ongoing")}
                      </span>
                    </div>
                    {devices.length > 0 && (
                      <div className="flex flex-col gap-1 col-span-2">
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Devices</span>
                        <div className="flex flex-wrap gap-1">
                          {devices.map((d) => (
                            <span key={d} className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300">
                              {d}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Confirmation Modals */}
      <AnimatedModal
        show={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title={t("common.manageCampaign.confirmStatusChange", "Confirm Status Change")}
        isConfirmation
        onConfirm={handleConfirmStatusChange}
        isConfirming={isActionLoading}
        confirmButtonColor={pendingStatus === APPLE_CAMPAIGN_STATUS.ENABLED ? "green" : "red"}
      >
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className={`p-3 rounded-full ${pendingStatus === APPLE_CAMPAIGN_STATUS.ENABLED ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>
            <Megaphone size={32} weight="duotone" />
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            {t("common.manageCampaign.statusChangeConfirmation", `Are you sure you want to ${pendingStatus === APPLE_CAMPAIGN_STATUS.ENABLED ? "enable" : "pause"} this campaign?`)}
          </p>
        </div>
      </AnimatedModal>

      <AnimatedModal
        show={showBudgetModal}
        onClose={() => setShowBudgetModal(false)}
        title={t("common.manageCampaign.confirmBudgetChange", "Confirm Budget Change")}
        isConfirmation
        onConfirm={handleConfirmBudgetChange}
        isConfirming={isActionLoading}
      >
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className="p-3 rounded-full bg-indigo-100 text-indigo-600">
            <Money size={32} weight="duotone" />
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              {t("common.manageCampaign.budgetChangeConfirmation", "Are you sure you want to update the daily budget?")}
            </p>
            <div className="flex items-center justify-center gap-3 font-bold text-lg">
              <span className="text-gray-400 line-through">
                {`${getCurrencySymbol(currency as CurrencyCode)}${(campaign?.daily_budget || 0).toLocaleString()}`}
              </span>
              <span className="text-gray-300">→</span>
              <span className="text-indigo-600">
                {getCurrencySymbol(currency as CurrencyCode)}
                {(parseFloat(pendingBudget) || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </AnimatedModal>

      <AnimatedModal
        show={showBidModal}
        onClose={() => setShowBidModal(false)}
        title={t("common.manageCampaign.confirmBidChange", "Confirm Bid Change")}
        isConfirmation
        onConfirm={handleConfirmBidChange}
        isConfirming={isActionLoading}
      >
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className="p-3 rounded-full bg-indigo-100 text-indigo-600">
            <CursorClick size={32} weight="duotone" />
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              {t("common.manageCampaign.bidChangeConfirmation", "Are you sure you want to update the default CPT bid?")}
            </p>
            <div className="flex items-center justify-center gap-3 font-bold text-lg">
              <span className="text-gray-400 line-through">
                {getCurrencySymbol(currency as CurrencyCode)}
                {(Number(campaign?.cpa || 0)).toLocaleString()}
              </span>
              <span className="text-gray-300">→</span>
              <span className="text-indigo-600">
                {getCurrencySymbol(currency as CurrencyCode)}
                {(parseFloat(pendingBid) || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </AnimatedModal>
    </BaseCampaignDetail>
  );
};

const InfoRow = ({ label, value, capitalize = false }: { label: string; value: React.ReactNode; capitalize?: boolean }) => (
  <div className="flex justify-between items-center py-2 border-b border-gray-50 dark:border-neutral-700 last:border-0 min-h-[44px]">
    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{label}</span>
    <span className={cn("text-sm font-bold text-gray-800 dark:text-gray-100 truncate pl-4", capitalize && "capitalize")}>
      {value}
    </span>
  </div>
);

export default AppleAdsCampaignDetail;
